// @flow

import React from "react";
import CarElement from "./CarElement";
import type FilterVehicleSeries from "../../models/FilterVehicleSeries";
import type Category from "../../models/Category";
import type {translate} from "../../i18next";
import type Brand from "../../models/Brand";
import "./CarSelectionBar.scss";
import {CSSTransition} from "react-transition-group";
import CarModelDropdown from "./CarModelDropdown";
import SwimLane from "../SwimLane";

export type CarSelectionBarProps = {
    t: translate,
    brand: Brand,
    filteredVehicleSeries: FilterVehicleSeries[],
    selectedVehicle: ?(FilterVehicleSeries | Category),
    selectionBreadcrumb: string,
    vehicleKey: ?string,
    resetVehicle: () => void,
    setVehicle: (string, string) => void,
};

export type CarSelectionBarState = {
    visibleVehicleModel: string,
    allowsClick: boolean,
}

class CarSelectionBar extends React.Component<CarSelectionBarProps, CarSelectionBarState> {
    constructor(props: CarSelectionBarProps) {
        super(props);
        this.state = {
            visibleVehicleModel: "",
            allowsClick: true,
        };
    };

    componentDidUpdate(prevProps) {
        if (this.props.filteredVehicleSeries === undefined) {
            this.setState({visibleVehicleModel: ""});
        }
    }

    toggleCarSelector = model => {
        this.setState({visibleVehicleModel: model});
    };

    setAllowsClick = (allowsClick: boolean) => {
        this.setState({allowsClick});
    };

    handleVehicleSelection = (series, model) => {
        this.props.setVehicle(series, model);
        this.setState({visibleVehicleModel: ""});
    };

    getPillLabel = () => {
        const {selectionBreadcrumb, selectedVehicle, brand, t} = this.props;
        if (selectionBreadcrumb) return selectionBreadcrumb;
        if (selectedVehicle) return selectedVehicle.title || selectedVehicle.name || "";
        const brandLabel = (brand && brand.name) || "";
        const fallback = t("sort.carline");
        return brandLabel ? `${brandLabel} ${fallback}` : fallback;
    };

    render() {
        const hasVehicleFilter = !!this.props.selectedVehicle || !!this.props.vehicleKey;
        if (!hasVehicleFilter && !this.props.filteredVehicleSeries.filter(vehicle => vehicle.categories.length > 0).length) {
            return null;
        }

        const pillLabel = hasVehicleFilter ? this.getPillLabel() : "";

        return (
            <div className={"hpm-carSelectionBar"} data-testid="hpm-carSelectionBar">
                <CSSTransition in={hasVehicleFilter} unmountOnExit={true} timeout={0}>
                    <button
                        className={"hpm-infoCar"}
                        onClick={this.props.resetVehicle}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                                this.props.resetVehicle();
                            }
                        }}
                        type={"button"}
                        tabIndex={0}>
                        <span className={"hpm-selectionBarCarTitle"}> {pillLabel}</span>
                        <div className="hpm-close"/>
                    </button>
                </CSSTransition>
                {/* Derived from the store, not set optimistically on click: a selection that
                    could not be resolved must leave the swimlane in place. */}
                {!hasVehicleFilter && (
                    <CSSTransition in={!this.props.selectedVehicle} unmountOnExit={false} timeout={0}>
                        <div className={"hpm-CarSelectorWrapper"}>
                            <SwimLane
                                key={this.props.filteredVehicleSeries.length}
                                setAllowsClick={this.setAllowsClick}
                                childrenCount={this.props.filteredVehicleSeries.length}
                                id={"hpm-carSeriesSwimlane"}>
                                {this.props.filteredVehicleSeries.map((vehicle: FilterVehicleSeries) => {
                                    const vehicleCategories = vehicle.categories || [];
                                    return <div
                                        key={vehicle.key}>
                                        <CarElement
                                            setVehicle={this.props.setVehicle} key={vehicle.key} id={vehicle.key}
                                            imgSrc={vehicle.imageUrl} name={vehicle.name}
                                            title={vehicle.title}
                                            selected={this.props.selectedVehicle && (vehicle.name === this.props.selectedVehicle.name)}
                                            vehicleModels={vehicleCategories}
                                            visibleVehicleModel={this.state.visibleVehicleModel}
                                            toggleVehicleSelector={this.toggleCarSelector}
                                            carRef={vehicle.carRef}
                                            allowsClick={this.state.allowsClick}
                                        />
                                        <CarModelDropdown
                                            key={vehicle.key + "_models"}
                                            vehicleSeries={vehicle}
                                            setVehicle={this.handleVehicleSelection}
                                            vehicleModels={vehicleCategories}
                                            isVisible={this.state.visibleVehicleModel === vehicle.name}
                                            toggleVehicleSelector={this.toggleCarSelector}
                                            carRef={vehicle.carRef}
                                            allowsClick={this.state.allowsClick}
                                        />
                                    </div>;
                                })}
                            </SwimLane>
                        </div>
                    </CSSTransition>
                )}
            </div>
        );
    }
}

export default CarSelectionBar;
